import { isUpcomingPeriod, periodQuery, ReviewPeriodProvider, useReviewPeriodLocked } from './reviewPeriod';
import { rememberCollectionsPeriod, rememberedCollectionsPeriod } from '../collections/collectionsPeriodMemory';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { getFacilityWorkspaceOverview, apiErrorMessage } from '../api/abfApi';
import { facilityWorkspacePath, isAbfWorkspaceKey } from '../routes';
import type {
  AbfWorkspaceKey,
  FacilityWorkspaceOverview,
} from '../types/facilityWorkspace';
import { ViewportToast, type ActionFeedbackState } from '../components/ActionFeedback';
import { AuthorityBadge } from '../components/AuthorityBadge';
import { ExplainMetricButton } from '../copilot/AbfCopilotLayout';
import { authorityValue, displayText, fmtDate, fmtMoney, fmtPct, freshnessSentence, plural } from '../presentation/format';
import OverviewWorkspace from './OverviewWorkspace';
import FacilityPhaseBadge from './FacilityPhaseBadge';
import ContractWorkspace from './ContractWorkspace';
import CollateralCycleWorkspace from './CollateralCycleWorkspace';
import FundingWorkspace from '../funding/FundingWorkspace';
import CollectionsWorkspace from '../collections/CollectionsWorkspace';
import MonitoringWorkspace from '../monitoring/MonitoringWorkspace';
import ValuationWorkspace from '../valuation/ValuationWorkspace';
import ReportingWorkspace from '../reporting/ReportingWorkspace';
import DocumentsWorkspace from '../documents/DocumentsWorkspace';
import ActivityWorkspace from '../activity/ActivityWorkspace';

const Page = styled.div`
  min-height: 100vh;
  padding: 1rem 1.25rem 2.5rem;
  color: #e2e8f0;
`;

const Shell = styled.div`
  width: min(100%, 100rem);
  margin: 0 auto;
`;

const ContextHeader = styled.header`
  position: sticky;
  /* Keep facility identity below the 64px app navigation and 37px context strip. */
  top: 101px;
  z-index: 40;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 16px;
  background: radial-gradient(circle at top right, rgba(14, 116, 144, 0.18), transparent 38%), rgba(5, 12, 24, 0.97);
  box-shadow: 0 18px 55px rgba(2, 6, 23, 0.42);
  backdrop-filter: blur(16px);
`;

const HeaderTop = styled.div`
  display: grid;
  grid-template-columns: minmax(19rem, 1.2fr) minmax(32rem, 2.8fr);
  gap: 1rem;
  padding: 1rem;

  @media (max-width: 1050px) { grid-template-columns: 1fr; }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7dd3fc;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 800;
`;

const IdentityTitle = styled.h1`
  margin: 0.55rem 0 0.2rem;
  color: #f8fafc;
  font-size: clamp(1.25rem, 2vw, 1.8rem);
  line-height: 1.15;
`;

const IdentityMeta = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  line-height: 1.5;
`;

const HeaderFacts = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(8.5rem, 1fr));
  gap: 0.55rem;

  @media (max-width: 780px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const HeaderFact = styled.div`
  min-width: 0;
  padding: 0.65rem 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.62);
`;

const FactLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  margin-bottom: 0.28rem;
  color: #64748b;
  font-size: 0.58rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  button, span[aria-hidden] { text-transform: none; letter-spacing: 0; font-weight: 600; }
`;

/** F-LR-045: the records behind the header facts, once, as a compact sources line (was the Overview's lineage grid). */
const Sources = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.45rem;
  grid-column: 1 / -1;
  margin-top: 0.1rem;
  color: #64748b;
  font-size: 0.58rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  > span:first-child { margin-right: 0.2rem; }
`;

const FactValue = styled.div<{ $tone?: 'default' | 'emerald' | 'amber' }>`
  overflow: hidden;
  color: ${p => p.$tone === 'emerald' ? '#6ee7b7' : p.$tone === 'amber' ? '#fcd34d' : '#f8fafc'};
  font-size: 0.84rem;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FactSubtext = styled.div`
  overflow: hidden;
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.62rem;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WorkspaceNav = styled.nav`
  display: flex;
  gap: 0.15rem;
  overflow-x: auto;
  padding: 0 0.75rem;
  border-top: 1px solid rgba(148, 163, 184, 0.13);
`;

const NavButton = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  padding: 0.75rem 0.68rem;
  border: 0;
  border-bottom: 2px solid ${p => p.$active ? '#38bdf8' : 'transparent'};
  background: transparent;
  color: ${p => p.$active ? '#e0f2fe' : '#94a3b8'};
  cursor: pointer;
  font-size: 0.68rem;
  font-weight: 800;
`;



const Panel = styled.section`
  padding: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  background: rgba(8, 16, 30, 0.78);
  box-shadow: 0 12px 36px rgba(2, 6, 23, 0.2);
`;














const Empty = styled.div`
  padding: 2.5rem 1rem;
  color: #94a3b8;
  text-align: center;
  font-size: 0.78rem;
`;

const workspaceTabs: Array<{ key: AbfWorkspaceKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'contract', label: 'Contract' },
  { key: 'collateral', label: 'Collateral' },
  { key: 'funding', label: 'Funding' },
  { key: 'collections', label: 'Collections' },
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'valuation', label: 'Valuation' },
  { key: 'reporting', label: 'Reporting' },
  { key: 'documents', label: 'Documents' },
  { key: 'activity', label: 'Activity' },
];

const ReviewContext = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px 24px; padding: 12px 20px;
  border-top: 1px solid rgba(148,163,184,.18); background: #101b2b;
  label { color: #a6b9ce; font-size: 12px; display: flex; align-items: center; gap: 10px; }
  select { padding: 8px 12px; border: 1px solid #57718b; border-radius: 8px; background: #1a2c40; color: #eef7ff; font: inherit; font-size: 14px; }
  select:focus-visible { outline: 2px solid #9cd7f7; outline-offset: 2px; }
  .dates { color: #b6c5d7; font-size: 12px; }
  .scope { flex: 1 1 320px; color: #a6b9ce; font-size: 12px; line-height: 1.6; }
`;
const FacilityWorkspacePage: React.FC = () => {
  const { facilityId: facilityIdParam, workspace: workspaceParam } = useParams();
  const facilityId = Number(facilityIdParam);
  const requestedWorkspace: AbfWorkspaceKey = isAbfWorkspaceKey(workspaceParam) ? workspaceParam : 'overview';
  // The Borrowing Base tab merged into Collateral (F-S2-UI-005); its paths still resolve.
  const workspace: AbfWorkspaceKey = requestedWorkspace === 'borrowing-base' ? 'collateral' : requestedWorkspace;
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (requestedWorkspace !== 'borrowing-base' || !Number.isFinite(facilityId)) return;
    const copy = new URLSearchParams(location.search);
    copy.set('collateralView', copy.get('borrowingBaseView') === 'details' ? 'details' : 'period');
    copy.delete('borrowingBaseView');
    navigate(`${facilityWorkspacePath(facilityId, 'collateral')}?${copy}`, { replace: true });
  }, [requestedWorkspace, facilityId, location.search, navigate]);
  const { user } = useAuth();
  const periodLocked = useReviewPeriodLocked();
  const [overview, setOverview] = useState<FacilityWorkspaceOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [localFeedback, setLocalFeedback] = useState<ActionFeedbackState | null>(null);

  const load = useCallback(async (notify = false) => {
    if (!Number.isInteger(facilityId) || facilityId <= 0) {
      setLoading(false);
      setFeedback({ tone: 'error', message: 'The facility link is invalid.' });
      return;
    }
    setLoading(true);
    if (notify) {
      const message = 'Refreshing facility authorities…';
      setFeedback({ tone: 'info', message });
      setLocalFeedback({ tone: 'info', message });
    } else {
      setLocalFeedback(null);
    }
    try {
      const result = await getFacilityWorkspaceOverview(facilityId);
      setOverview(result);
      if (notify) {
        const message = `Facility authorities refreshed as of ${new Date(result.generatedAt).toLocaleTimeString()}.`;
        setFeedback({ tone: 'success', message });
        setLocalFeedback({ tone: 'success', message });
      }
    } catch (error) {
      const message = apiErrorMessage(error, 'Unable to load the facility workspace.');
      setFeedback({ tone: 'error', message });
      setLocalFeedback({ tone: 'error', message });
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  const handleWorkspaceFeedback = useCallback((nextFeedback: ActionFeedbackState | null) => {
    setFeedback(nextFeedback);
    // Child workspaces own their local read model, while this page owns the
    // facility-wide authority header. Refresh that parent projection after a
    // successful child mutation so availability, exceptions, marks, and the
    // next-best action cannot remain stale until a full page reload.
    if (nextFeedback?.tone === 'success') void load(false);
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const query = new URLSearchParams(location.search);
  const requestedPeriod = query.get('period') || query.get('periodId');
  const periods = overview?.reviewPeriods || [];
  const businessDate = overview?.businessDate || '';
  const selectedPeriod = periods.find(p => p.id === Number(requestedPeriod));
  const invalidPeriod = Boolean(requestedPeriod && overview && !selectedPeriod);
  useEffect(() => {
    if (!overview || Number(overview.facility.facilityId) !== facilityId || requestedWorkspace === 'borrowing-base') return;
    if (requestedPeriod) {
      const selected = periods.find(p => p.id === Number(requestedPeriod));
      if (selected) {
        rememberCollectionsPeriod(facilityId, selected.id);
        const canonical = new URLSearchParams(location.search);
        if (canonical.has('periodId')) { canonical.set('period', String(selected.id)); canonical.delete('periodId'); navigate(`${location.pathname}?${canonical}`, {replace:true}); }
      }
      return; // Invalid links remain explicit; never silently substitute another month.
    }
    const remembered = rememberedCollectionsPeriod(facilityId);
    const earliestUnclosed = periods.filter(p => p.end < businessDate && p.status === 'OPEN')
      .slice().sort((a, b) => a.start.localeCompare(b.start))[0];
    const preferred = periods.find(p => p.id === remembered)
      || earliestUnclosed || periods.find(p => p.end < businessDate) || periods.find(p => !isUpcomingPeriod(p.start, businessDate)) || periods[0];
    if (preferred) {
      const next = new URLSearchParams(location.search); next.set('period', String(preferred.id));
      navigate(`${location.pathname}?${next}`, {replace:true});
    }
  }, [overview, facilityId, requestedPeriod, location.pathname, location.search, navigate, requestedWorkspace]);
  const choosePeriod = (id: number) => {
    if (periodLocked || !periods.some(p => p.id === id)) return;
    rememberCollectionsPeriod(facilityId, id);
    navigate(`${location.pathname}?${periodQuery(location.search, id)}`);
  };
  const currentAndPast = periods.filter(p => !isUpcomingPeriod(p.start, businessDate));
  const upcomingPeriods = periods.filter(p => isUpcomingPeriod(p.start, businessDate)).slice().reverse();
  const periodScoped = ['collateral','collections','monitoring','reporting','documents','activity'].includes(workspace);
  const contextReady = !invalidPeriod && (!periods.length || (Boolean(selectedPeriod) && !query.has('periodId')));
  const currency = overview?.facility.currency || 'USD';

  const openNextAction = () => {
    if (!overview || overview.nextAction.code === 'NO_ACTION') return;
    navigate(`${facilityWorkspacePath(facilityId, overview.nextAction.workspace)}${location.search}`);
    setLocalFeedback({
      tone: 'info',
      message: `${overview.nextAction.title} is available through the existing controlled workflow.`,
    });
  };

  if (loading && !overview) return <Page><Shell><Panel><Empty>Loading authoritative facility context…</Empty></Panel></Shell></Page>;
  if (!overview) return (
    <Page><Shell><Panel><Empty>Facility context could not be loaded. Use the visible notification for details.</Empty></Panel></Shell>
      <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
    </Page>
  );

  const { facility, contract, collateral, borrowingBase, officialPosition, availability, exceptions } = overview;
  // F-LR-044/045: every headline fact lives here once; the Overview tab no longer repeats them.
  const commitment = contract.totalCommitment ?? facility.totalCommitment;
  const baseAuthority = borrowingBase.alignmentStatus === 'STALE' ? 'STALE' : borrowingBase.authority;
  const sources: Array<{ label: string; authority: string | null | undefined; lineage: Record<string, unknown> }> = [
    { label: 'Contract', authority: contract.authority, lineage: contract as unknown as Record<string, unknown> },
    { label: 'Tape', authority: collateral.authority, lineage: collateral as unknown as Record<string, unknown> },
    { label: 'Base', authority: baseAuthority, lineage: borrowingBase as unknown as Record<string, unknown> },
    { label: 'Certificate', authority: overview.reportedBorrowingBase?.authority, lineage: (overview.reportedBorrowingBase || {}) as Record<string, unknown> },
    { label: 'IBOR', authority: officialPosition.authority, lineage: officialPosition as unknown as Record<string, unknown> },
    { label: 'Book of record', authority: overview.exposure?.authority, lineage: (overview.exposure || {}) as Record<string, unknown> },
    { label: 'Availability', authority: availability.authority || 'COMPUTED', lineage: availability as unknown as Record<string, unknown> },
  ];

  return (
    <Page>
      <Shell>
        <ContextHeader>
          <HeaderTop>
            <div>
              <BackButton onClick={() => navigate('/portfolio-management?tab=credit-loans&sub=abf')}>
                <ArrowLeft size={14} /> ABF facilities
              </BackButton>
              <IdentityTitle>{displayText(facility.facilityName, 'ABF facility')}</IdentityTitle>
              <IdentityMeta>
                {displayText(facility.facilityCode, 'No facility code')} · {displayText(facility.obligorName, 'Borrower not recorded')}<br />
                {displayText(facility.collateralType, 'Collateral type not recorded')} · {displayText(facility.status, 'No status')}
                {/* Facility phase (structure stream): <PhaseBadge /> mounts here, after the status. */}
              </IdentityMeta>
              <FacilityPhaseBadge phase={overview.phase} />
            </div>
            <HeaderFacts>
              <HeaderFact><FactLabel>Active contract</FactLabel><FactValue>{contract.versionNumber == null ? 'No active version' : `Version ${contract.versionNumber}`}</FactValue><FactSubtext>{contract.effectiveFrom ? `Effective ${fmtDate(contract.effectiveFrom)}` : 'No effective date'}</FactSubtext></HeaderFact>
              <HeaderFact><FactLabel><span>Commitment</span><ExplainMetricButton scope="current" metric="COMMITMENT" /></FactLabel><FactValue>{fmtMoney(commitment, currency, 0, 'Not stated')}</FactValue><FactSubtext>{facility.drawnAmount == null ? 'From the active contract' : `${fmtMoney(facility.drawnAmount, currency)} drawn`}</FactSubtext></HeaderFact>
              <HeaderFact><FactLabel>Latest released tape</FactLabel><FactValue>{collateral.asOfDate ? fmtDate(collateral.asOfDate) : 'None released'}</FactValue><FactSubtext>{freshnessSentence(collateral)}</FactSubtext></HeaderFact>
              <HeaderFact><FactLabel><span>Approved borrowing base</span><ExplainMetricButton scope="current" metric="BORROWING_BASE" /></FactLabel><FactValue>{fmtMoney(borrowingBase.amount, currency, 0, 'None approved')}</FactValue><FactSubtext>{borrowingBase.approvedAt ? `Approved ${fmtDate(borrowingBase.approvedAt)}${borrowingBase.alignmentStatus === 'STALE' ? ' · behind the tape' : ''}` : 'No approved run'}</FactSubtext></HeaderFact>
              <HeaderFact><FactLabel><span>Official IBOR exposure</span><ExplainMetricButton scope="current" metric="EXPOSURE" /></FactLabel><FactValue>{fmtMoney(officialPosition.exposure, currency, 0, 'No official position')}</FactValue><FactSubtext>{officialPosition.asOfDate ? `Position as of ${fmtDate(officialPosition.asOfDate)} · not commitment` : 'Position authority · not commitment'}</FactSubtext></HeaderFact>
              <HeaderFact><FactLabel><span>Availability</span><ExplainMetricButton scope="current" metric="AVAILABILITY" /></FactLabel><FactValue $tone={availability.gate || availability.unsupportedReason ? 'amber' : 'emerald'}>{fmtMoney(availability.amount, currency, 0, 'No figure')}</FactValue><FactSubtext title={availability.gate || undefined}>{availability.unsupportedReason
                ? availability.unsupportedReason.split(':')[0] + (availability.holds ? ` · also held by ${plural(availability.holds, 'control')}` : '')
                : availability.gate
                ? (availability.holds ? `held by ${plural(availability.holds, 'control')}` : availability.gate.split(':')[0]) + (availability.beforeHolds != null ? ` · ${fmtMoney(availability.beforeHolds, currency)} before` : '')
                : `${fmtMoney(availability.activeReservations, currency)} reserved`}</FactSubtext></HeaderFact>
              <HeaderFact><FactLabel>Current exceptions</FactLabel><FactValue $tone={exceptions.blocking > 0 ? 'amber' : 'emerald'}>{exceptions.total}</FactValue><FactSubtext>{exceptions.total === 0 ? 'None open' : `${exceptions.blocking} blocking`}</FactSubtext></HeaderFact>
              <HeaderFact><FactLabel>Official position mark</FactLabel><FactValue>{fmtPct(officialPosition.markPct, 2, 'No mark')}</FactValue><FactSubtext>{officialPosition.marketValue == null ? 'No market value' : `${fmtMoney(officialPosition.marketValue, currency)} market value`}</FactSubtext></HeaderFact>
              <Sources aria-label="Authority and lineage">
                <span>Authority and lineage</span>
                {sources.map(source => <AuthorityBadge key={source.label} label={source.label} authority={authorityValue(source.authority)} lineage={source.lineage} />)}
              </Sources>
            </HeaderFacts>
          </HeaderTop>
          <ReviewContext aria-label="Facility review context">
            <label htmlFor="facility-review-period">Review period
              <select id="facility-review-period" aria-label="Facility review period" value={selectedPeriod?.id || ''} disabled={periodLocked || !periods.length} onChange={e => choosePeriod(Number(e.target.value))}>
                {!selectedPeriod && <option value="">{invalidPeriod ? 'Choose a valid period' : periods.length ? 'Selecting period…' : 'No periods configured'}</option>}
                <optgroup label="Current & past">{currentAndPast.map(p => <option key={p.id} value={p.id}>{p.label}{p.start <= businessDate && p.end >= businessDate ? ' · Current' : ''}</option>)}</optgroup>
                {upcomingPeriods.length > 0 && <optgroup label="Upcoming">{upcomingPeriods.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}</optgroup>}
              </select>
            </label>
            <span className="dates">Business date <strong>{fmtDate(businessDate)}</strong>{selectedPeriod && <> · Reviewing {fmtDate(selectedPeriod.start)} – {fmtDate(selectedPeriod.end)}</>}</span>
            <span className="scope">{periodLocked ? 'An action is in progress. Period selection is temporarily locked.' : invalidPeriod ? 'This period does not belong to the facility. Select a period above.' : workspace === 'contract' ? 'Contract version is selected explicitly below. The review period does not change legal authority.' : !periodScoped ? 'Current facility information. The review period does not change today’s position, funding availability or mark.' : workspace === 'monitoring' ? 'Tests follow the review period. Active consequences and exceptions show current facility controls.' : workspace === 'reporting' && query.get('quarter') ? `Reviewing quarter ${query.get('quarter')}. Your monthly review period is retained above.` : 'The selected period follows you across the review tabs. Header figures remain current, with their source dates.'}</span>
          </ReviewContext>
          <WorkspaceNav aria-label="Facility workspaces">
            {workspaceTabs.map(tab => (
              <NavButton key={tab.key} $active={tab.key === workspace} disabled={periodLocked} onClick={() => { const next = new URLSearchParams(location.search); if (tab.key !== 'reporting') next.delete('quarter'); navigate(`${facilityWorkspacePath(facilityId, tab.key)}?${next}`); }}>
                {tab.label}
              </NavButton>
            ))}
          </WorkspaceNav>
        </ContextHeader>

        {!contextReady ? <Panel><Empty>{invalidPeriod ? 'Select a valid review period above to continue.' : 'Preparing the review period…'}</Empty></Panel> : <React.Fragment key={periodScoped ? `${facilityId}:${selectedPeriod?.id || 'none'}` : `${facilityId}:current`}>{workspace === 'overview' ? (
          <OverviewWorkspace
            facilityId={facilityId}
            overview={overview}
            userRole={user?.role}
            loading={loading}
            localFeedback={localFeedback}
            onRefresh={() => load(true)}
            onNextAction={openNextAction}
          />
        ) : workspace === 'contract' ? (
          <ContractWorkspace
            facilityId={facilityId}
            userRole={user?.role}
            onFeedback={handleWorkspaceFeedback}
          />
        ) : workspace === 'collateral' ? (
          <CollateralCycleWorkspace key={facilityId}
            facilityId={facilityId}
            currency={facility.currency}
            userRole={user?.role}
            servicerName={facility.servicerName}
            onFeedback={handleWorkspaceFeedback}
          />
        ) : workspace === 'funding' ? (
          <FundingWorkspace
            facilityId={facilityId}
            currency={facility.currency}
            userRole={user?.role}
            onFeedback={handleWorkspaceFeedback}
          />
        ) : workspace === 'collections' ? (
          <CollectionsWorkspace
            facilityId={facilityId}
            currency={facility.currency}
            userRole={user?.role}
            onFeedback={handleWorkspaceFeedback}
          />
        ) : workspace === 'monitoring' ? (
          <MonitoringWorkspace facilityId={facilityId} currency={facility.currency} onFeedback={handleWorkspaceFeedback}
            contractVersion={{ recordId: overview.contract.recordId, versionNumber: overview.contract.versionNumber }} />
        ) : workspace === 'valuation' ? (
          <ValuationWorkspace officialPosition={overview.officialPosition} facilityId={facilityId} currency={facility.currency} onFeedback={handleWorkspaceFeedback} />
        ) : workspace === 'reporting' ? (
          <ReportingWorkspace facilityId={facilityId} onFeedback={handleWorkspaceFeedback} />
        ) : workspace === 'documents' ? (
          <DocumentsWorkspace facilityId={facilityId} userRole={user?.role} onFeedback={handleWorkspaceFeedback} />
        ) : (
          <ActivityWorkspace facilityId={facilityId} onFeedback={handleWorkspaceFeedback} />
        )}</React.Fragment>}
      </Shell>
      <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
    </Page>
  );
};

export default function FacilityWorkspaceWithReviewPeriod() { return <ReviewPeriodProvider><FacilityWorkspacePage /></ReviewPeriodProvider>; }
